import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  Calculator, 
  Variable, 
  Shapes, 
  Sigma, 
  ArrowRight, 
  Sparkles,
  Brain,
  BookOpen,
  Clock,
  CheckCircle2
} from "lucide-react";

const topics = [
  {
    id: "arithmetic",
    name: "Arithmetic",
    description: "Master basic operations and number theory",
    icon: Calculator,
    color: "icon-arithmetic",
    examples: ["Addition", "Subtraction", "Fractions", "Percentages"]
  },
  {
    id: "algebra",
    name: "Algebra",
    description: "Solve equations and work with variables",
    icon: Variable,
    color: "icon-algebra",
    examples: ["Linear Equations", "Quadratics", "Functions"]
  },
  {
    id: "geometry",
    name: "Geometry",
    description: "Explore shapes, angles, and spatial reasoning",
    icon: Shapes,
    color: "icon-geometry",
    examples: ["Triangles", "Circles", "Area & Volume"]
  },
  {
    id: "calculus",
    name: "Calculus",
    description: "Dive into limits, derivatives, and integrals",
    icon: Sigma,
    color: "icon-calculus",
    examples: ["Limits", "Derivatives", "Integrals"]
  }
];

const features = [
  {
    icon: Brain,
    title: "AI-Powered Solutions",
    description: "Get step-by-step explanations powered by GPT-5.2"
  },
  {
    icon: BookOpen,
    title: "Practice Mode",
    description: "Generate unlimited practice problems by topic"
  },
  {
    icon: Clock,
    title: "Solution History",
    description: "Review and revisit your past solutions anytime"
  },
  {
    icon: CheckCircle2,
    title: "All Skill Levels",
    description: "From basic arithmetic to advanced calculus"
  }
];

export default function LandingPage() {
  return (
    <div className="page-transition">
      {/* Hero Section */}
      <section className="relative overflow-hidden hero-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="flex flex-col items-center text-center space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
              <Sparkles className="w-4 h-4" />
              AI-Powered Math Tutoring
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight font-['Outfit']" data-testid="hero-title">
              Master Math with{" "}
              <span className="gradient-text">DeepCore AI</span>
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed">
              Your personal AI math tutor. Get instant step-by-step solutions, 
              practice problems, and clear explanations for any math topic.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link to="/chat" data-testid="start-learning-btn">
                <Button 
                  size="lg" 
                  className="rounded-full shadow-lg shadow-primary/25 hover:scale-105 transition-transform px-8"
                >
                  Start Learning
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/practice" data-testid="try-practice-btn">
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="rounded-full hover:bg-accent px-8"
                >
                  Try Practice Mode
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-primary/10 rounded-full blur-2xl" />
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-secondary/20 rounded-full blur-3xl" />
      </section>

      {/* Topics Section */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left mb-12">
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight font-['Outfit'] mb-4">
              Explore Math Topics
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Choose a topic to start learning. Our AI tutor covers everything from 
              basic arithmetic to advanced calculus.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="topics-grid">
            {topics.map((topic) => {
              const Icon = topic.icon;
              return (
                <Link 
                  key={topic.id} 
                  to={`/chat/${topic.id}`}
                  data-testid={`topic-card-${topic.id}`}
                >
                  <Card className="topic-card h-full border-slate-200 dark:border-slate-800 hover:border-primary/50 cursor-pointer">
                    <CardContent className="p-6 space-y-4">
                      <div className={`w-12 h-12 rounded-xl ${topic.color} flex items-center justify-center`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold font-['Outfit'] mb-2">
                          {topic.name}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          {topic.description}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {topic.examples.map((example) => (
                            <span 
                              key={example}
                              className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground"
                            >
                              {example}
                            </span>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/5184950/pexels-photo-5184950.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Student studying"
                className="rounded-3xl shadow-xl"
                data-testid="features-image"
              />
            </div>
            
            <div className="space-y-8">
              <div>
                <h2 className="text-4xl md:text-5xl font-semibold tracking-tight font-['Outfit'] mb-4">
                  Why DeepCore Math?
                </h2>
                <p className="text-lg text-muted-foreground">
                  Designed for learners of all ages and skill levels.
                </p>
              </div>
              
              <div className="grid gap-6" data-testid="features-list">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div 
                      key={index} 
                      className="flex gap-4 items-start"
                      data-testid={`feature-${index}`}
                    >
                      <div className="feature-icon shrink-0">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold font-['Outfit'] mb-1">
                          {feature.title}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-primary text-primary-foreground overflow-hidden">
            <CardContent className="p-8 md:p-12">
              <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                <div className="text-center md:text-left">
                  <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] mb-4">
                    Ready to Master Mathematics?
                  </h2>
                  <p className="text-primary-foreground/80 text-lg max-w-xl">
                    Start solving problems now with DeepCore Math AI. No signup required.
                  </p>
                </div>
                <Link to="/chat" data-testid="cta-start-btn">
                  <Button 
                    size="lg" 
                    variant="secondary"
                    className="rounded-full px-8 shadow-lg hover:scale-105 transition-transform"
                  >
                    Start Now
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
